import { useEffect, useMemo, useState, useCallback, memo } from 'react'
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import axios from 'axios'
import { Crown, Users, ChevronDown, MapPin, UserCircle2, Plus } from 'lucide-react'
import clsx from 'clsx'
import { buildApiUrl } from '../../../config/api'
import { Spinner } from '../../../components/ui'

interface TreeNode {
  id: number | null
  name: string
  nickname?: string | null
  photo_url?: string | null
  city_name?: string | null
  state_code?: string | null
  is_root?: boolean
  matched?: boolean
  direct_count: number
  total_count: number
  voters_count?: number
  children: TreeNode[]
}

interface OrgFilters {
  geo_city_ids: string[]
  area_city_ids: string[]
  gender: string
  has_user: string
  can_register: string
}

interface Props {
  campaignId?: number | null
  search?: string
  filters?: OrgFilters
  onSelectLeader?: (leaderId: number) => void
  onAddChild?: (parentLeaderId: number | null) => void
  refreshKey?: number
}

// Dimensões dos nós e espaçamento do layout em árvore
const NODE_W = 240
const NODE_H = 104
const X_STEP = NODE_W + 36
const Y_STEP = NODE_H + 68

type NodeData = {
  tree: TreeNode
  nodeKey: string
  hasChildren: boolean
  collapsed: boolean
  selected: boolean
  onToggle: (key: string) => void
  onSelect: (leaderId: number | null) => void
  onAddChild: (parentLeaderId: number | null) => void
}

function keyOf(n: TreeNode): string {
  return n.is_root ? 'root' : `l-${n.id}`
}

// ── Nó customizado ──
const OrgNode = memo(({ data }: NodeProps<Node<NodeData>>) => {
  const { tree, nodeKey, hasChildren, collapsed, selected, onToggle, onSelect, onAddChild } = data
  const isRoot = !!tree.is_root
  const isDimmed = tree.matched === false // ancestral só de caminho (não casa com o filtro)
  const initials = tree.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div
      onClick={() => onSelect(tree.id)}
      className={clsx(
        'relative rounded-2xl border bg-white shadow-sm transition-all cursor-pointer select-none',
        'hover:shadow-md hover:-translate-y-0.5',
        selected ? 'border-brand-500 ring-2 ring-brand-500/20' : 'border-gray-200',
        isRoot && 'bg-gradient-to-br from-brand-500 to-brand-700 border-brand-700 text-white',
        isDimmed && !isRoot && 'opacity-45 grayscale',
      )}
      style={{ width: NODE_W, minHeight: NODE_H }}
    >
      <Handle type="target" position={Position.Top} className="!bg-transparent !border-0 !w-2 !h-2" />

      {!isRoot && (
        <span className="absolute top-2 right-2.5 text-[10px] font-mono font-medium text-gray-300 leading-none">
          #{tree.id}
        </span>
      )}

      <div className="flex items-center gap-3 p-3.5">
        <div className={clsx(
          'w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden font-semibold',
          isRoot ? 'bg-white/20 text-white' : 'bg-brand-50 text-brand-600',
        )}>
          {tree.photo_url
            ? <img src={tree.photo_url} alt={tree.name} className="w-full h-full object-cover" />
            : isRoot ? <Crown className="w-5 h-5" /> : <span className="text-sm">{initials}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <p className={clsx('text-sm font-semibold truncate', isRoot ? 'text-white' : 'text-gray-900')}>
            {tree.name}
          </p>
          {isRoot ? (
            <p className="text-xs text-white/70 mt-0.5">Candidato · {tree.total_count} lideranças</p>
          ) : (
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-500 truncate">
                {tree.city_name ? `${tree.city_name}/${tree.state_code}` : 'Sem cidade'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Rodapé com métricas */}
      {!isRoot && (
        <div className="flex items-center gap-3 px-3.5 pb-3 -mt-1">
          {tree.direct_count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-600">
              <UserCircle2 className="w-3.5 h-3.5" />
              {tree.total_count} sub
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5" />
            {(tree.voters_count ?? 0).toLocaleString('pt-BR')} eleitores
          </span>
        </div>
      )}

      {/* Botão expandir/recolher */}
      {hasChildren && (
        <button
          onClick={e => { e.stopPropagation(); onToggle(nodeKey) }}
          className={clsx(
            'absolute -bottom-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full border flex items-center justify-center shadow-sm transition-colors z-10',
            'bg-white border-gray-300 text-gray-600 hover:bg-brand-50 hover:text-brand-600 hover:border-brand-300',
          )}
          title={collapsed ? 'Expandir subordinados' : 'Recolher'}
        >
          {collapsed
            ? <span className="flex items-center text-[10px] font-bold leading-none">{tree.direct_count}</span>
            : <ChevronDown className="w-4 h-4" />}
        </button>
      )}

      {/* Botão adicionar liderança subordinada */}
      <button
        onClick={e => { e.stopPropagation(); onAddChild(tree.id) }}
        className={clsx(
          'absolute -bottom-3 right-3 w-7 h-7 rounded-full flex items-center justify-center shadow-sm transition-colors z-10',
          'bg-brand-500 text-white hover:bg-brand-600',
        )}
        title={isRoot ? 'Adicionar liderança de topo' : 'Adicionar liderança subordinada a esta'}
      >
        <Plus className="w-4 h-4" />
      </button>

      <Handle type="source" position={Position.Bottom} className="!bg-transparent !border-0 !w-2 !h-2" />
    </div>
  )
})
OrgNode.displayName = 'OrgNode'

const nodeTypes = { org: OrgNode }

export default function LeadersOrgChart({ campaignId, search = '', filters, onSelectLeader, onAddChild, refreshKey }: Props) {
  const [tree, setTree] = useState<TreeNode | null>(null)
  const [loading, setLoading] = useState(true)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [selectedKey, setSelectedKey] = useState<string>('root')
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeData>>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  const toggle = useCallback((key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const selectNode = useCallback((leaderId: number | null) => {
    setSelectedKey(leaderId == null ? 'root' : `l-${leaderId}`)
    if (leaderId != null) onSelectLeader?.(leaderId)
  }, [onSelectLeader])

  const addChild = useCallback((parentLeaderId: number | null) => {
    onAddChild?.(parentLeaderId)
  }, [onAddChild])

  // Buscar árvore (com debounce para a busca por texto)
  const filterKey = JSON.stringify(filters ?? {})
  useEffect(() => {
    const run = () => {
      setLoading(true)
      const params: any = {}
      if (campaignId) params.campaign_id = campaignId
      if (search.trim()) params.search = search.trim()
      if (filters) {
        if (filters.geo_city_ids.length) params.geo_city_id = filters.geo_city_ids.join(',')
        if (filters.area_city_ids.length) params.area_city_id = filters.area_city_ids.join(',')
        if (filters.gender) params.gender = filters.gender
        if (filters.has_user) params.has_user = filters.has_user
        if (filters.can_register) params.can_register = filters.can_register
      }
      axios.get(buildApiUrl('/v1/leaders/tree'), { params })
        .then(res => setTree(res.data.data.root))
        .catch(() => setTree(null))
        .finally(() => setLoading(false))
    }
    const t = setTimeout(run, search.trim() ? 350 : 0)
    return () => clearTimeout(t)
  }, [campaignId, search, filterKey, refreshKey])

  const isFiltering = !!search.trim() || !!(filters && (
    filters.geo_city_ids.length || filters.area_city_ids.length || filters.gender || filters.has_user || filters.can_register
  )) || !!campaignId

  // Calcular layout (posições) a partir da árvore + estado de colapso
  const layout = useMemo(() => {
    if (!tree) return { rfNodes: [] as Node<NodeData>[], rfEdges: [] as Edge[] }
    const rfNodes: Node<NodeData>[] = []
    const rfEdges: Edge[] = []
    let leafX = 0

    const walk = (node: TreeNode, depth: number, parentKey: string | null): number => {
      const key = keyOf(node)
      const hasChildren = node.children.length > 0
      // Durante filtro, expandir tudo para não esconder resultados
      const isCollapsed = !isFiltering && collapsed.has(key)
      const visibleChildren = isCollapsed ? [] : node.children

      let x: number
      if (visibleChildren.length === 0) {
        x = leafX * X_STEP
        leafX++
      } else {
        const childXs = visibleChildren.map(c => walk(c, depth + 1, key))
        x = (childXs[0] + childXs[childXs.length - 1]) / 2
      }

      rfNodes.push({
        id: key,
        type: 'org',
        position: { x, y: depth * Y_STEP },
        data: {
          tree: node,
          nodeKey: key,
          hasChildren,
          collapsed: isCollapsed,
          selected: selectedKey === key,
          onToggle: toggle,
          onSelect: selectNode,
          onAddChild: addChild,
        },
        draggable: false,
      })

      if (parentKey) {
        rfEdges.push({
          id: `${parentKey}->${key}`,
          source: parentKey,
          target: key,
          type: 'smoothstep',
          style: { stroke: '#cbd5e1', strokeWidth: 1.5 },
        })
      }
      return x
    }

    walk(tree, 0, null)
    return { rfNodes, rfEdges }
  }, [tree, collapsed, selectedKey, toggle, selectNode, addChild, isFiltering])

  useEffect(() => {
    setNodes(layout.rfNodes)
    setEdges(layout.rfEdges)
  }, [layout, setNodes, setEdges])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh] rounded-2xl border border-gray-200 bg-gray-50">
        <Spinner />
      </div>
    )
  }

  if (!tree || tree.total_count === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] rounded-2xl border border-gray-200 bg-gray-50 text-center px-6">
        <Crown className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-gray-600 font-medium">Nenhuma liderança para exibir no organograma</p>
        <p className="text-gray-400 text-sm mt-1">Cadastre lideranças e defina quem responde por quem.</p>
      </div>
    )
  }

  return (
    <div className="h-[74vh] rounded-2xl border border-gray-200 bg-gray-50 overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.1}
        maxZoom={1.6}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false}
        nodesDraggable={false}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.5} color="#d1d5db" />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={n => (n.id === 'root' ? '#465fff' : '#c7d2fe')}
          nodeStrokeWidth={2}
          pannable
          zoomable
          className="!bg-white !border !border-gray-200 !rounded-lg"
        />
      </ReactFlow>
    </div>
  )
}
